import { createdTeamSchema, issuedCredentialsSchema, publicUserSchema, teamCompositionSchema, teamSchema, } from '@ghp/domain';
import { z } from 'zod';
import { bearer } from './http.js';
const teamListSchema = z.array(teamSchema);
const compositionListSchema = z.array(teamCompositionSchema);
const candidateListSchema = z.array(publicUserSchema);
/**
 * L'équipe et sa composition (M12, M22). **Tout est ouvert au back-office** — composer une équipe
 * est un geste de pilotage, pas de gestion de comptes : le prototype validé montre un gestionnaire
 * sur cet écran. La garde vit côté serveur (`apps/api/src/teams/teams.controller.ts`) ; rien ici ne
 * la rejoue, un filtre de plus dans le navigateur ne protégerait que l'affichage.
 *
 * ⚠️ **`candidates` existe à cause de cette asymétrie** : `GET /users` est resté **admin seul**
 * (décision GHP du 03/09/2026 sur les comptes), alors qu'un gestionnaire doit pouvoir peupler son
 * sélecteur d'agents. La route étroite rend les comptes affectables et **rien d'autre** — pas
 * l'annuaire, pas les comptes de bureau, aucun geste de cycle de vie.
 *
 * Le jeton voyage **en argument**, comme partout ailleurs — pas de jeton de service côté
 * back-office, seulement celui de la session en cours.
 */
export function createTeamsEndpoints(http) {
    return {
        /**
         * `201` : le **groupe** entier — l'équipe, ses deux agents, le compte partagé de sa tablette
         * et son rattachement (M22), en une transaction côté serveur.
         *
         * L'appelant fournit **les deux agents**, et s'il le veut les communes cochées de son secteur
         * (`inseeCodes`, écrites dans la même transaction) : le nom de l'équipe, le login du compte et
         * son mot de passe sont tous dérivés côté serveur (M6, M22).
         *
         * ⚠️ La réponse porte le mot de passe **en clair, une seule fois** — seul son hachage est
         * stocké. À l'écran de le montrer, à personne de le journaliser.
         */
        create(accessToken, input) {
            return http.postJson('/teams', input, createdTeamSchema, bearer(accessToken));
        },
        /**
         * `200` : toutes les équipes, triées par `name` côté serveur (D24) — jamais en mémoire ici.
         * Pas de pagination : le sélecteur d'affectation les veut toutes.
         *
         * ⚠️ **Sans leurs membres** : c'est `composition` qu'il faut pour ça. La liste déroulante
         * d'affectation n'a que faire du personnel, et lui faire payer deux jointures à chaque
         * ouverture serait le coût d'un seul écran, réparti sur tous.
         */
        list(accessToken) {
            return http.get('/teams', teamListSchema, bearer(accessToken));
        },
        /**
         * `200` : les équipes **avec leurs membres et leur charge de contrôles** — la grille de
         * l'écran « Équipes ». Charge comptée en base (D24), pas de pagination pour la même raison
         * que `list`.
         *
         * ⚠️ `members` est **plat** : les agents et le compte partagé y sont mêlés, et c'est voulu —
         * `partitionTeamMembers` de `@ghp/domain` les range, et rend au passage une ligne au rôle
         * inattendu au lieu de la faire disparaître (M21).
         */
        listComposition(accessToken) {
            return http.get('/teams/composition', compositionListSchema, bearer(accessToken));
        },
        /**
         * `200` : les comptes qu'on peut faire entrer dans une équipe — agents et comptes partagés
         * (M12) —, triés par `login` côté serveur et **filtrés en base** (D24).
         *
         * ⚠️ Les comptes **fermés** y sont : un agent en congé reste affectable (M21), et c'est
         * l'écran qui le signale. Ne pas les refiltrer ici.
         */
        listCandidates(accessToken) {
            return http.get('/teams/candidates', candidateListSchema, bearer(accessToken));
        },
        /**
         * Corrige l'identité d'une équipe : son **nom**, et l'**identifiant de connexion de sa
         * tablette** (M22, M6). Le corps ne porte que ce qui change, et la réponse est la composition
         * entière — comme les deux écritures de membres.
         *
         * ⚠️ **Cette route existe parce que `PATCH /users/:id` ne peut pas le faire** :
         * `updateUserSchema` exclut le `login` et la route est admin seul. Même asymétrie que
         * `candidates`.
         *
         * ⚠️ **Deux choses à dire à l'utilisateur avant de valider un nouvel identifiant**, et l'écran
         * est le seul à pouvoir : la session en cours **n'est pas coupée** (le jeton vaut par
         * `user.id`, M23) — c'est la **prochaine** connexion avec l'ancien identifiant qui échoue, et
         * quelqu'un doit donc transmettre le nouveau à l'équipe.
         *
         * ⚠️ **`409` à afficher mot pour mot** : l'identifiant déjà pris (`User.login` est unique,
         * M6), et l'équipe qui n'a pas de compte tablette à renommer.
         */
        update(accessToken, teamId, patch) {
            return http.patchJson(`/teams/${teamId}`, patch, teamCompositionSchema, bearer(accessToken));
        },
        /**
         * **Remplace** la couverture d'une équipe — la liste entière des communes retenues, pas un
         * delta —, et rend sa composition. `PUT`, donc rejouable sans effet de bord.
         *
         * ⚠️ Le seul refus est le `404` d'une commune inconnue du référentiel, à afficher mot pour
         * mot.
         */
        updateCoverage(accessToken, teamId, input) {
            return http.putJson(`/teams/${teamId}/coverage`, input, teamCompositionSchema, bearer(accessToken));
        },
        /**
         * `201` : donne sa tablette à une équipe qui n'en a pas — le compte partagé et son
         * rattachement (M22), en une transaction côté serveur.
         *
         * ⚠️ **Une réparation, pas un second chemin de création.** Une équipe créée depuis le
         * 04/09/2026 naît avec son compte ; celles qui n'en ont pas sont **antérieures**, et sans ce
         * geste elles n'ont ni identifiant, ni mot de passe, ni moyen d'en obtenir.
         *
         * ⚠️ `login` est **facultatif** : le serveur le dérive des initiales des deux agents quand il
         * le peut (M6), et **refuse** en dessous de deux plutôt que d'inventer `eq.jl`. C'est
         * justement le cas de ces équipes-là — d'où le champ de saisie de l'écran.
         *
         * ⚠️ La réponse porte le mot de passe **en clair, une seule fois**, comme `create`.
         */
        createAccount(accessToken, teamId, input) {
            return http.postJson(`/teams/${teamId}/account`, input, createdTeamSchema, bearer(accessToken));
        },
        /**
         * Tire un nouveau mot de passe pour la tablette de l'équipe et **coupe ses sessions**
         * (M6/M23). Même réserve que `create` sur la réponse : le mot de passe ne se lit qu'ici.
         *
         * ⚠️ **Jumelle de `users.resetPassword`, et non un doublon.** Celle-ci désigne le compte **par
         * son équipe** : c'est ce qui permet de l'ouvrir au back-office sans ouvrir `users/`, qui
         * reste admin seul (M6) — un gestionnaire n'atteint par ici que des comptes `team`, jamais
         * celui d'un admin.
         */
        resetAccountPassword(accessToken, teamId) {
            return http.postJson(`/teams/${teamId}/account/reset-password`, {}, issuedCredentialsSchema, bearer(accessToken));
        },
        /**
         * Affecte un compte à une équipe — un agent, ou le compte partagé qui la représente (M22).
         *
         * `200` et non `201` : la réponse est la **composition entière** de l'équipe, pas la ligne
         * `Membership`, et l'appel peut n'avoir rien inséré. C'est cette composition qui dit à l'écran
         * ce qui reste possible : la place restante, le plafond atteint, la charge inchangée.
         *
         * ⚠️ **Trois `409` à afficher mot pour mot**, le serveur seul sachant pourquoi il refuse et
         * quoi faire ensuite : les deux agents déjà en place (M12), le compte partagé déjà rattaché à
         * une autre équipe, et l'équipe déjà représentée par un autre compte (M22). Un compte **déjà
         * membre**, en revanche, n'est pas un refus — le geste a abouti (M21).
         */
        addMember(accessToken, teamId, userId) {
            return http.postJson(`/teams/${teamId}/members`, { userId }, teamCompositionSchema, bearer(accessToken));
        },
        /**
         * Retire un compte d'une équipe. **Idempotent** : un retrait déjà fait rend la même
         * composition qu'un retrait effectif, sans `404` — deux onglets ouverts sur le même écran ne
         * doivent pas se contredire.
         *
         * ⚠️ **Ce n'est pas la fermeture d'un compte**, qui vit dans `users` et ne supprime rien non
         * plus (M6) : le `User` reste, il n'appartient plus à cette équipe. Et rien n'est historisé —
         * M12 acte qu'il n'y a pas d'historique de composition d'équipe.
         */
        removeMember(accessToken, teamId, userId) {
            return http.deleteJson(`/teams/${teamId}/members/${userId}`, teamCompositionSchema, bearer(accessToken));
        },
    };
}
//# sourceMappingURL=teams.js.map